import { ReactElement, useCallback, useMemo } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import SectionLoading from '@components/SectionLoading';
import SortOrder from '@components/SortableTable/components/SortOrder';
import { PLACEMENT } from '@components/CustomTextInput';
import { ORDERS, ROUTES } from '@shared/constants';
import { DEFAULT_PAGE_NUMBER, DEFAULT_OFFSET, ROLES } from '../consts';
import {
  CellUsers,
  HeaderColumnUsers,
  NoItemsUsers,
  TableHeaderUsers,
  TableRowUsers,
  TableWrapperUsers,
  TableListUsers,
  LoadingOverlayUsers,
  SearchContainer,
  StyledSearchIcon,
  StyledCustomTextInput,
} from './styles';
import { putOrder, fetchStaff, setSearchText, putStaffPageNumber, putStaffOffset } from '../actions';
import { UserData } from '../../UserPage/user-page-wrapper/components/UserInformation/types';
import { USER_PAGE_TYPES } from '../../UserPage/consts';
import CustomTooltip, { placementTypes } from '../../../../../components/CustomTooltip';
import { getTableColumn } from '../helpers';

type TableColumn = {
  title: string;
  getDisplay: (data: UserData) => ReactElement;
  width: string;
  sortable: boolean;
  sortField: string;
};

const calculateSpread = (config: TableColumn[]): string => config.map(({ width }) => width || 'auto').join(' ');

type HeaderUserSortableTableProps = {
  spread: string;
  config: TableColumn[];
  orderHandler: () => void;
  order: string;
};

const HeaderUserSortableTable = ({ spread, config, orderHandler, order }: HeaderUserSortableTableProps) => (
  <TableHeaderUsers spread={spread} className="tableHeader">
    {config.map(({ title, sortable }, index) => {
      return (
        <HeaderColumnUsers key={`${title}`} className="headerCell" isLast={index === config.length - 1}>
          <CellUsers
            noPadding
            titlesUppercase
            pointer={sortable}
            onClick={() => {
              if (sortable) {
                orderHandler();
              }
            }}
          >
            {title}
          </CellUsers>
          {sortable && <SortOrder order={order} />}
        </HeaderColumnUsers>
      );
    })}
  </TableHeaderUsers>
);

type RowUserProps = {
  item: UserData;
  spread: string;
  handleRowSelect: (id: string) => void;
  config: TableColumn[];
};

const RowUser = ({ item, spread, handleRowSelect, config }: RowUserProps) => {
  const rowProps = {
    spread,
    onClick: () => handleRowSelect(item.id),
    key: item.id,
    isHighlighted: false,
  };
  return (
    <TableRowUsers className="row" {...rowProps}>
      {config.map(({ getDisplay }, colIndex) => (
        <CustomTooltip
          placement={placementTypes.BOTTOM}
          text={getDisplay && getDisplay(item)}
          tiny
          key={`${item.id}-${colIndex}`}
        >
          <CellUsers className="cell" isLast={colIndex === config.length - 1}>
            {getDisplay && getDisplay(item)}
          </CellUsers>
        </CustomTooltip>
      ))}
    </TableRowUsers>
  );
};

export const StaffSortableTable = () => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const handleRowSelect = useCallback((id: string) => {
    push(generatePath(ROUTES.STAFF.PAGE, { id, pageType: USER_PAGE_TYPES.INFORMATION }));
  }, []);
  const { loading, list, order, searchText } = useSelector((state: RootStateOrAny) =>
    state.getIn(['staffList']).toJS(),
  );
  const { roles } = useSelector((state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS());

  const dispatch = useDispatch();
  const isTechAdmin = useMemo(() => roles.includes(ROLES.TECH_ADMIN), [roles]);

  const config: TableColumn[] = useMemo(() => getTableColumn(isTechAdmin) as unknown as TableColumn[], [isTechAdmin]);
  const spread = useMemo(() => calculateSpread(config), [config]);
  const orderHandler = useCallback(() => {
    const orderToPut = order === ORDERS.ASC ? ORDERS.DESC : ORDERS.ASC;
    dispatch(putOrder(orderToPut));
  }, [order]);

  const debouncedSearch = useCallback(
    debounce(() => {
      dispatch(fetchStaff());
    }, 500),
    [dispatch],
  );

  const handleFilterInput = useCallback(
    (event: any) => {
      const { value } = event.target;
      dispatch(setSearchText(value));
      dispatch(putStaffPageNumber(DEFAULT_PAGE_NUMBER));
      dispatch(putStaffOffset(DEFAULT_OFFSET));
      debouncedSearch();
    },
    [dispatch, debouncedSearch],
  );

  return (
    <TableWrapperUsers>
      <SearchContainer>
        <StyledCustomTextInput
          value={searchText}
          onChange={handleFilterInput}
          Icon={StyledSearchIcon}
          iconPlacement={PLACEMENT.RIGHT}
          name="staff-filter-input"
          id="staff-filter-input"
          placeholder={t('Common.Placeholders.searchUser')}
        />
      </SearchContainer>
      <div style={{ position: 'relative' }}>
        {loading && (
          <LoadingOverlayUsers>
            <SectionLoading />
          </LoadingOverlayUsers>
        )}
        <HeaderUserSortableTable spread={spread} config={config} orderHandler={orderHandler} order={order} />
        <TableListUsers>
          {!list.length ? (
            <NoItemsUsers>{t('Informative.Negative.noItems')}</NoItemsUsers>
          ) : (
            <div>
              {list.map((item: UserData) => (
                <RowUser spread={spread} handleRowSelect={handleRowSelect} config={config} item={item} key={item.id} />
              ))}
            </div>
          )}
        </TableListUsers>
      </div>
    </TableWrapperUsers>
  );
};
